"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.findProperty = findProperty;
exports.resultSort = resultSort;
exports.matchQuery = matchQuery;
exports.checkMixinKey = checkMixinKey;
const fast_equals_1 = require("fast-equals");
const component_1 = __importDefault(require("./component"));
const objvalue_1 = require("./objvalue");
const predicate_1 = require("./predicate");
/**
 * @public
 */
function findProperty(objects, propertyKey, value) {
    if ((0, predicate_1.isPredicate)(value)) {
        const preds = (0, predicate_1.createPredicates)(value, propertyKey);
        for (const pred of preds) {
            objects = pred(objects);
        }
        return objects;
    }
    const result = [];
    for (const object of objects) {
        const val = (0, objvalue_1.getObjectValue)(propertyKey, object);
        if (val === value || (val == null && value == null) || isArrayValueCheck(val, value)) {
            result.push(object);
        }
    }
    return result;
}
function isArrayValueCheck(val, value) {
    if (!Array.isArray(val) || Array.isArray(value)) {
        return false;
    }
    // Object value: match if any element contains all its keys (server `@>` semantics),
    // e.g. { relations: { _id, _class } } against relations: [{ _id, _class }].
    // Reference `includes` fails for object literals, breaking live-query reactivity.
    if (typeof value === 'object' && value !== null) {
        return val.some((v) => containsSubset(v, value));
    }
    return val.includes(value);
}
function containsSubset(el, value) {
    if (el === value)
        return true;
    if (typeof value !== 'object' || value === null || typeof el !== 'object' || el === null) {
        return el === value;
    }
    for (const k in value) {
        if (!(0, fast_equals_1.deepEqual)(el[k], value[k]))
            return false;
    }
    return true;
}
function getEnumValue(key, _class, hierarchy, obj, _enum) {
    const tkey = checkMixinKey(key, _class, hierarchy);
    const value = (0, objvalue_1.getObjectValue)(tkey, obj);
    const index = _enum.enumValues.findIndex((p) => p === value);
    return index === -1 ? _enum.enumValues.length : index;
}
/**
 * @public
 */
function resultSort(result, sortOptions, _class, hierarchy, modelDb) {
    const enums = getEnums(_class, sortOptions, hierarchy, modelDb);
    const sortFunc = (a, b) => {
        for (const key in sortOptions) {
            const _enum = enums[key];
            const aValue = _enum !== undefined ? getEnumValue(key, _class, hierarchy, a, _enum) : getValue(key, a, _class, hierarchy);
            const bValue = _enum !== undefined ? getEnumValue(key, _class, hierarchy, b, _enum) : getValue(key, b, _class, hierarchy);
            const result = getSortingResult(aValue, bValue, sortOptions[key]);
            if (result !== 0)
                return result;
        }
        return 0;
    };
    result.sort(sortFunc);
}
function mapSortingValue(order, val) {
    if (typeof order !== 'object') {
        return val;
    }
    for (const r of order.cases) {
        if (typeof r.query === 'object') {
            const q = r.query;
            if (q.$in?.includes(val) ?? false) {
                return r.index;
            }
            if (q.$nin !== undefined && !q.$nin.includes(val)) {
                return r.index;
            }
            if (q.$ne !== undefined && q.$ne !== val) {
                return r.index;
            }
        }
        if (r.query === val) {
            return r.index;
        }
    }
}
function getSortingResult(aValue, bValue, order) {
    let res = 0;
    if (typeof aValue === 'undefined') {
        return typeof bValue === 'undefined' ? 0 : -1;
    }
    if (typeof bValue === 'undefined') {
        return 1;
    }
    const orderOrder = typeof order === 'object' ? order.order : order;
    if (Array.isArray(aValue) && Array.isArray(bValue)) {
        res =
            (aValue.map((it) => mapSortingValue(order, it)).sort((a, b) => (a - b) * orderOrder)[0] ?? 0) -
                (bValue.map((it) => mapSortingValue(order, it)).sort((a, b) => (a - b) * orderOrder)[0] ?? 0);
    }
    else {
        const aaValue = mapSortingValue(order, aValue);
        const bbValue = mapSortingValue(order, bValue);
        res = typeof aaValue === 'string' ? aaValue.localeCompare(bbValue) : aaValue - bbValue;
    }
    return res * orderOrder;
}
function getEnums(_class, sortOptions, hierarchy, modelDb) {
    const res = {};
    for (const key in sortOptions) {
        const attr = hierarchy.findAttribute(_class, key);
        if (attr !== undefined) {
            if (attr !== undefined) {
                if (attr.type._class === component_1.default.class.EnumOf) {
                    const ref = attr.type.of;
                    const enu = modelDb.findAllSync(component_1.default.class.Enum, { _id: ref });
                    res[key] = enu[0];
                }
            }
        }
    }
    return res;
}
function getValue(key, obj, _class, hierarchy) {
    const tkey = checkMixinKey(key, _class, hierarchy);
    let value = (0, objvalue_1.getObjectValue)(tkey, obj);
    if (typeof value === 'object' && !Array.isArray(value)) {
        value = JSON.stringify(value);
    }
    return value;
}
/**
 * @public
 */
function matchQuery(docs, query, clazz, hierarchy, skipLookup = false) {
    const baseClass = hierarchy.getBaseClass(clazz);
    let result = docs.filter((r) => hierarchy.isDerived(r._class, baseClass));
    if (baseClass !== clazz) {
        result = docs.filter((r) => hierarchy.hasMixin(r, clazz));
    }
    for (const key in query) {
        if (skipLookup && key.startsWith('$lookup.')) {
            continue;
        }
        const value = query[key];
        const tkey = checkMixinKey(key, clazz, hierarchy);
        result = findProperty(result, tkey, value);
        if (result.length === 0) {
            break;
        }
    }
    return result;
}
/**
 * @public
 */
function checkMixinKey(key, clazz, hierarchy) {
    if (!key.includes('.')) {
        try {
            const attr = hierarchy.findAttribute(clazz, key);
            if (attr !== undefined && hierarchy.isMixin(attr.attributeOf)) {
                // It is mixin
                key = attr.attributeOf + '.' + key;
            }
        }
        catch (err) {
            // ignore, if
        }
    }
    return key;
}
//# sourceMappingURL=query.js.map